/*
 * Open tabs grouped by client. Cases and the client profile share a group
 * keyed by the client (foreign national) id, so two clients with the same
 * name stay two groups; every page tab is its own group. Group order is the
 * order of first appearance in the tab list and never depends on which tab
 * is active. Pure so the rules are unit-tested.
 */
import type { AttentionItem } from "@/features/agentAttention/types";

import type { AppTab } from "./types";

export type TabGroup = {
  /** `client:<clientId>`, else the tab's own id. */
  key: string;
  clientId?: string;
  tabs: AppTab[];
};

export const groupKeyOf = (tab: Pick<AppTab, "id" | "kind" | "clientId">) =>
  tab.kind !== "page" && tab.clientId ? `client:${tab.clientId}` : tab.id;

export function groupTabs(tabs: AppTab[]): TabGroup[] {
  const groups = new Map<string, TabGroup>();
  for (const tab of tabs) {
    const key = groupKeyOf(tab);
    const found = groups.get(key);
    if (found) found.tabs.push(tab);
    else
      groups.set(key, {
        key,
        ...(tab.kind !== "page" && tab.clientId
          ? { clientId: tab.clientId }
          : {}),
        tabs: [tab]
      });
  }
  return Array.from(groups.values());
}

export const groupOf = (
  groups: TabGroup[],
  tabId: string | null
): TabGroup | null =>
  (tabId && groups.find((g) => g.tabs.some((t) => t.id === tabId))) || null;

export const isPageGroup = (group: TabGroup): boolean =>
  group.tabs.length === 1 && group.tabs[0].kind === "page";

const PLACEHOLDER_TITLES = new Set([
  "Case",
  "Client",
  "Unknown client",
  "No Access"
]);

/** The client's name (the profile tab's title first), or the page's title. */
export const groupLabel = (group: TabGroup): string => {
  const named = (t: AppTab) => !PLACEHOLDER_TITLES.has(t.title);
  const client = group.tabs.find((t) => t.kind === "client" && named(t));
  return (
    client?.title ?? group.tabs.find(named)?.title ?? group.tabs[0]?.title ?? ""
  );
};

/** "K. Zlaoui" → "KZ"; "Priya" → "PR". */
export const nameInitials = (name: string): string => {
  const words = name.match(/\p{L}[\p{L}'-]*/gu) ?? [];
  const first = words[0] ?? name;
  const last = words[words.length - 1] ?? "";
  if (words.length > 1) return `${first[0]}${last[0]}`.toUpperCase();
  return first.slice(0, 2).toUpperCase();
};

/** What the group's avatar draws: a client's photo or initials, or a page icon. */
export const groupMark = (
  group: TabGroup
): Pick<AppTab, "kind" | "pageId" | "initials" | "avatarUrl" | "title"> => {
  if (isPageGroup(group)) return group.tabs[0];
  const withPhoto = group.tabs.find((t) => t.avatarUrl);
  const withInitials = group.tabs.find((t) => t.initials);
  const title = groupLabel(group);
  return {
    kind: "client",
    title,
    avatarUrl: withPhoto?.avatarUrl,
    initials: withInitials?.initials ?? nameInitials(title)
  };
};

export const groupSubtitle = (group: TabGroup): string | undefined =>
  group.tabs.find((t) => t.kind === "client" && t.subtitle)?.subtitle ??
  group.tabs.find((t) => t.subtitle)?.subtitle;

/** A segment in the open group: the case's workflow, Profile, or the page. */
export const segmentLabel = (tab: AppTab): string =>
  tab.kind === "case"
    ? tab.workflow || "Case"
    : tab.kind === "client"
    ? "Profile"
    : tab.title;

/** A row in the hover card: "O-1A case", "Profile". */
export const rowLabel = (tab: AppTab): string =>
  tab.kind === "case"
    ? tab.workflow
      ? `${tab.workflow} case`
      : "Case"
    : tab.kind === "client"
    ? "Profile"
    : tab.title;

/** The group's most recently used tab; clicking its tile opens this one. */
export const lastUsedTab = (group: TabGroup, recency: string[]): AppTab => {
  const ids = new Set(group.tabs.map((t) => t.id));
  const id = recency.find((x) => ids.has(x));
  return group.tabs.find((t) => t.id === id) ?? group.tabs[0];
};

/** Every signal in the group, and whether any of its agents is working. */
export const groupState = (
  group: TabGroup,
  signalsFor: (tabId: string) => AttentionItem[],
  isRunning: (tabId: string) => boolean
): { signals: AttentionItem[]; running: boolean } => ({
  signals: group.tabs.flatMap((t) => signalsFor(t.id)),
  running: group.tabs.some((t) => isRunning(t.id))
});

/** "Viewed 5m ago" for a tab with no agent activity; empty when never stamped. */
export const viewedAgo = (
  visitedAt: number | undefined,
  now: number
): string => {
  if (!visitedAt) return "";
  const mins = Math.floor((now - visitedAt) / 60_000);
  if (mins < 1) return "Viewed just now";
  if (mins < 60) return `Viewed ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Viewed ${hours}h ago`;
  return `Viewed ${Math.floor(hours / 24)}d ago`;
};

/** "Morocco · b. 1991" from a client record; empty parts drop out. */
export const clientSubtitle = (
  client: {
    country_of_citizenship?: string | null;
    birthday?: string | null;
  } | null
): string | undefined => {
  const country = client?.country_of_citizenship?.trim();
  const year = client?.birthday?.match(/\b(19|20)\d{2}\b/)?.[0];
  const parts = [country, year ? `b. ${year}` : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
};
